package ui.app.assign.detail.wheel.editor.pages

import androidx.compose.animation.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import api.ResourceOrientation
import api.ResourceType
import apps.App
import compose.assets.Svg
import compose.assets.svg.*
import kotlinx.coroutines.launch
import org.burnoutcrew.reorderable.*
import ssoft.frontend.generated.resources.*
import tools.EFieldRegularLong
import tools.GlobalObjectShare
import tools.rememberString
import ui.app.assign.detail.wheel.AppWheelRendererGame
import ui.app.assign.detail.wheel.AppWheelRendererReward
import ui.app.assign.detail.wheel.editor.AppEditorWheelScope
import ui.app.assign.detail.wheel.editor.widgets.FieldSlider
import ui.app.assign.detail.wheel.editor.widgets.MobileDualView
import ui.resource.ResourceListingArgs
import ui.widget.ColorField
import ui.widget.LazyIcon
import ui.widget.LazyImage
import ui.widget.Space


@Composable
fun AppEditorWheelScope.AppWheelPage03_Items(nnvl: AppEditorWheelScope.Wrapper) {
    if (display.isMobileOrSmall) MobileView03(nnvl)
    else DesktopView03(nnvl)
}

@Composable
private fun AppEditorWheelScope.DesktopView03(nnvl: AppEditorWheelScope.Wrapper) {
    Row(modifier = Modifier.fillMaxSize()) {
        PreviewItems(nnvl, Modifier.weight(1F))
        ContentItems(nnvl, Modifier.weight(1.5F))
    }
}

@Composable
private fun AppEditorWheelScope.MobileView03(nnvl: AppEditorWheelScope.Wrapper) {
    MobileDualView(
        labelPrimary = Res.string.layer_bottom_wheel_tab_edit,
        labelSecondary = Res.string.layer_bottom_wheel_tab_preview,
        bodyPrimary = {
            ContentItems(nnvl, Modifier.fillMaxSize())
        },
        bodySecondary = {
            PreviewItems(nnvl, Modifier.fillMaxSize())
        }
    )
}

@Composable
private fun AppEditorWheelScope.ContentItems(
    nnvl: AppEditorWheelScope.Wrapper,
    weight: Modifier
) {
    val reorderState = rememberReorderableLazyListState(
        onMove = { from, to -> nnvl.items.swapOrder(from.index, to.index) },
        canDragOver = { draggedOver: ItemPosition, dragging: ItemPosition ->
            draggedOver.key?.toString()?.contains("::") != true
        })
    Box(weight.fillMaxSize()) {
        Column(Modifier.fillMaxSize()) {
            Box(Modifier.fillMaxWidth()) {
                ButtonAdd(
                    nnvl = nnvl,
                    reorderState = reorderState,
                    padding = Modifier.align(Alignment.Center)
                        .padding(16.dp)
                )
            }
            LazyColumn(
                state = reorderState.listState,
                modifier = Modifier.fillMaxSize()
                    .reorderable(reorderState),
                contentPadding = PaddingValues(
                    start = 16.dp,
                    end = 16.dp,
                    bottom = 64.dp
                ),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {

                itemsIndexed(
                    items = nnvl.items.state.sortedBy { it.data.position },
                    key = { _, it -> it.key },
                ) { index, item ->
                    ReorderableItem(
                        reorderableState = reorderState,
                        key = item.key,
                        index = index
                    ) { isDragging ->
                        Item(index, nnvl, item, reorderState)
                    }
                }
                item(key = "::next") {
                    Box(Modifier.fillMaxWidth()) {
                        ButtonNext(
                            Modifier.align(Alignment.Center)
                                .padding(top = 24.dp)
                        ) {

                            if (nnvl.items.state.any { it.counter.err }) {
                                showMessage(Res.string.layer_bottom_wheel_validation_items_counter)
                            } else true
                        }
                    }
                }
            }
        }

        VerticalScrollbar(
            modifier = Modifier.fillMaxHeight()
                .align(Alignment.CenterEnd)
                .padding(4.dp),
            adapter = rememberScrollbarAdapter(reorderState.listState)
        )
    }
}

@Composable
private fun ButtonAdd(
    nnvl: AppEditorWheelScope.Wrapper,
    padding: Modifier,
    reorderState: ReorderableLazyListState
) {
    val scope = rememberCoroutineScope()

    Button(
        onClick = {
            scope.launch {
                nnvl.items.addItem(reorderState.listState)
            }
        },
        modifier = padding
    ) {
        Icon(
            imageVector = Svg.Plus,
            contentDescription = null,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
        Space(4.dp)
        Text(
            text = Res.string.layer_bottom_wheel_page_theme_add_item.rememberString(),
            fontWeight = FontWeight.Bold,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
    }
}

@Composable
private fun AppEditorWheelScope.Item(
    index: Int,
    nnvl: AppEditorWheelScope.Wrapper,
    item: AppEditorWheelScope.ItemsWrapper.Item,
    reorderState: ReorderableLazyListState
) {

    val isExpanded = remember {
        mutableStateOf(false)
    }
    val itemPickerKeyIcon = remember(item.key) {
        "wheel:item:icon:${item.key}"
    }

    val itemPickerKeyReward = remember(item.key) {
        "wheel:item:reward:${item.key}"
    }

    GlobalObjectShare.Consumer(itemPickerKeyIcon)
    GlobalObjectShare.Consumer(itemPickerKeyReward)

    val isLand = remember {
        nnvl.orientation.state == App.Wheel.Theme.ORIENTATION_LAND
    }

    if (display.isMobileOrSmall) {
        ItemInnerMobile(
            index = index,
            nnvl = nnvl,
            item = item,
            reorderState = reorderState,
            isExpanded = isExpanded,
            itemPickerKeyIcon = itemPickerKeyIcon,
            itemPickerKeyReward = itemPickerKeyReward,
            isLand = isLand
        )
    } else {
        ItemInnerDesktop(
            index = index,
            nnvl = nnvl,
            item = item,
            reorderState = reorderState,
            isExpanded = isExpanded,
            itemPickerKeyIcon = itemPickerKeyIcon,
            itemPickerKeyReward = itemPickerKeyReward
        )
    }
}

@Composable
private fun AppEditorWheelScope.ItemInnerMobile(
    index: Int,
    nnvl: AppEditorWheelScope.Wrapper,
    item: AppEditorWheelScope.ItemsWrapper.Item,
    reorderState: ReorderableLazyListState,
    isExpanded: MutableState<Boolean>,
    itemPickerKeyIcon: String,
    itemPickerKeyReward: String,
    isLand: Boolean
) {
    Box {
        Row(modifier = Modifier.padding(top = 16.dp, start = 8.dp)) {
            Card(
                colors = CardDefaults.cardColors(MaterialTheme.colorScheme.surfaceContainer),
                shape = RoundedCornerShape(16.dp),
                modifier = Modifier.weight(1F)
            ) {
                Column {
                    ItemName(
                        nnvl = nnvl,
                        item = item,
                        weight = Modifier.fillMaxWidth()
                    )
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        ItemIcon(
                            item = item,
                            imagePickerKey = itemPickerKeyIcon,
                            align = Modifier.align(Alignment.CenterVertically)
                        )

                        ItemCounter(nnvl, item, Modifier.weight(1F).align(Alignment.CenterVertically))
                        ItemEnpander(nnvl, item, Modifier.align(Alignment.CenterVertically)) {
                            isExpanded.value = !isExpanded.value
                        }

                    }
                    AnimatedVisibility(
                        visible = isExpanded.value,
                        modifier = Modifier.fillMaxWidth(),
                        enter = fadeIn() + expandVertically(),
                        exit = fadeOut() + shrinkVertically()
                    ) {
                        Column(
                            modifier = Modifier.padding(8.dp),
                            verticalArrangement = Arrangement.spacedBy(4.dp)
                        ) {

                            if (nnvl.orientation.state == App.Wheel.Theme.ORIENTATION_LAND) {
                                ItemReward(
                                    nnvl = nnvl,
                                    item = item,
                                    itemPickerKeyReward = itemPickerKeyReward,
                                    boxModifier = Modifier.fillMaxWidth()
                                )
                                ItemColors(item, true, modifier = Modifier.fillMaxWidth())
                            } else {
                                Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                    ItemColors(item, false, Modifier.weight(1F))
                                    ItemReward(
                                        nnvl = nnvl,
                                        item = item,
                                        itemPickerKeyReward = itemPickerKeyReward,
                                        boxModifier = if (isLand) Modifier.width(256.dp) else Modifier.width(192.dp)
                                    )
                                }
                            }
                        }
                    }
                }

            }
            if (index > 2) {
                IconButton(
                    onClick = { nnvl.items.removeItem(item) },
                    modifier = Modifier.padding(top = 8.dp)
                ) {
                    Icon(Svg.DeleteEmpty, null)
                }
            }
        }
        Box(
            Modifier
                .size(32.dp)
                .background(MaterialTheme.colorScheme.onSurface, CircleShape)
                .clip(CircleShape)
                .detectReorder(reorderState)

        ) {
            Text(
                text = (index + 1).toString(),
                modifier = Modifier.align(Alignment.Center),
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.surface
            )
        }
    }
}

@Composable
private fun AppEditorWheelScope.ItemInnerDesktop(
    index: Int,
    nnvl: AppEditorWheelScope.Wrapper,
    item: AppEditorWheelScope.ItemsWrapper.Item,
    reorderState: ReorderableLazyListState,
    isExpanded: MutableState<Boolean>,
    itemPickerKeyIcon: String,
    itemPickerKeyReward: String
) {
    Row {
        InlineReorder(index, reorderState)
        Card(
            colors = CardDefaults.cardColors(MaterialTheme.colorScheme.surfaceContainer),
            shape = RoundedCornerShape(16.dp),
            modifier = Modifier.weight(1F)
        ) {
            Column {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    ItemIcon(
                        item = item,
                        imagePickerKey = itemPickerKeyIcon,
                        align = Modifier.padding(start = 8.dp).align(Alignment.CenterVertically)
                    )
                    ItemName(
                        nnvl = nnvl,
                        item = item,
                        weight = Modifier.align(Alignment.CenterVertically)
                            .weight(1F)
                            .padding(vertical = 4.dp)
                    )
                    ItemCounter(nnvl, item, Modifier.align(Alignment.CenterVertically))
                    ItemEnpander(nnvl, item, Modifier.align(Alignment.CenterVertically)) {
                        isExpanded.value = !isExpanded.value
                    }

                }
                AnimatedVisibility(
                    visible = isExpanded.value,
                    modifier = Modifier.fillMaxWidth(),
                    enter = fadeIn() + expandVertically(),
                    exit = fadeOut() + shrinkVertically()
                ) {
                    Row(
                        modifier = Modifier.padding(8.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        ItemSliders(item, Modifier.weight(1F))
                        if (nnvl.orientation.state == App.Wheel.Theme.ORIENTATION_LAND) {
                            Column(
                                Modifier.width(256.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                ItemReward(
                                    nnvl = nnvl,
                                    item = item,
                                    itemPickerKeyReward = itemPickerKeyReward,
                                    boxModifier = Modifier.fillMaxWidth()
                                )
                                ItemColors(
                                    item = item,
                                    horizontalOrder = false,
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                        } else {
                            ItemColors(item)
                            ItemReward(
                                nnvl = nnvl,
                                item = item,
                                itemPickerKeyReward = itemPickerKeyReward,
                                boxModifier = Modifier.width(192.dp)
                            )
                        }
                    }
                }
            }

        }
        if (index > 2) {
            IconButton(
                onClick = { nnvl.items.removeItem(item) },
                modifier = Modifier.padding(top = 8.dp)
            ) {
                Icon(Svg.DeleteEmpty, null)
            }
        }
    }
}

@Composable
private fun InlineReorder(index: Int, reorderState: ReorderableLazyListState) {
    Box(
        Modifier
            .padding(top = 12.dp)
            .size(48.dp)
            .background(MaterialTheme.colorScheme.onSurface, CircleShape)
            .clip(CircleShape)
            .detectReorder(reorderState)

    ) {
        Text(
            text = (index + 1).toString(),
            modifier = Modifier.align(Alignment.Center),
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.surface
        )
    }
}


@Composable
private fun AppEditorWheelScope.ItemIcon(
    item: AppEditorWheelScope.ItemsWrapper.Item,
    imagePickerKey: String,
    align: Modifier
) {
    val isExpanded = remember {
        mutableStateOf(false)
    }

    Box(
        align.padding(start = 4.dp)
            .size(48.dp)
            .clip(RoundedCornerShape(4.dp))
            .clickable {
                isExpanded.value = true
            }.padding(4.dp)
    ) {
        when {
            item.iconSvg.state != ResourceType.NO_ICON -> LazyIcon(
                id = item.iconSvg.state,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Fit
            )

            item.iconImage.state != ResourceType.NO_IMAGE -> LazyImage(
                id = item.iconImage.state,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Fit
            )

            else -> Icon(
                imageVector = Svg.Image_plus,
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                tint = MaterialTheme.colorScheme.onSurface
            )
        }
        DropdownMenu(
            expanded = isExpanded.value,
            modifier = Modifier.clip(CardDefaults.shape),
            onDismissRequest = { isExpanded.value = false }) {
            AppEditorWheelScope.ImagePickerVariants.all.fastForEach { value ->
                DropdownMenuItem(
                    text = {
                        Text(value.label.rememberString())
                    },
                    onClick = {
                        isExpanded.value = false
                        when (value) {
                            AppEditorWheelScope.ImagePickerVariants.Companion.Icon -> {
                                GlobalObjectShare.launchIconPicker(
                                    key = imagePickerKey,
                                    nav = nav
                                ) {
                                    item.iconSvg.set(it.id)
                                    item.iconImage.set(ResourceType.NO_IMAGE)
                                }
                            }

                            AppEditorWheelScope.ImagePickerVariants.Companion.Image -> {
                                GlobalObjectShare.launchImagePicker(
                                    key = imagePickerKey,
                                    nav = nav,
                                    args = ResourceListingArgs(
                                        companyId = args.companyId,
                                        pickerMode = true,
                                        restrictionType = ResourceType.IMAGE.mask,
                                    )
                                ) {
                                    it.firstOrNull()?.also {
                                        item.iconImage.set(it.id)
                                        item.iconSvg.set(ResourceType.NO_ICON)
                                    }
                                }
                            }

                            AppEditorWheelScope.ImagePickerVariants.Companion.None -> {
                                item.resetIcon()
                            }
                        }
                    })
            }
        }
    }
}

@Composable
private fun ItemName(
    nnvl: AppEditorWheelScope.Wrapper,
    item: AppEditorWheelScope.ItemsWrapper.Item,
    weight: Modifier
) {
    TextField(
        readOnly = !nnvl.canEdit,
        value = item.name.text,
        isError = item.name.err,
        onValueChange = { item.name.set(it) },
        modifier = weight.focusRequester(item.nameFocus),
        placeholder = { Text(Res.string.layer_bottom_wheel_page_items_name.rememberString()) })
}

@Composable
private fun ItemCounter(
    nnvl: AppEditorWheelScope.Wrapper,
    item: AppEditorWheelScope.ItemsWrapper.Item,
    align: Modifier
) {
    TextField(
        readOnly = !nnvl.canEdit,
        value = item.counter.text,
        isError = item.counter.err,
        onValueChange = { item.counter.set(it) },
        modifier = align.width(128.dp),
        label = { Text(Res.string.layer_bottom_wheel_page_items_counter.rememberString()) })
}


@Composable
private fun ItemEnpander(
    nnvl: AppEditorWheelScope.Wrapper,
    item: AppEditorWheelScope.ItemsWrapper.Item,
    align: Modifier,
    onClick: () -> Unit
) {
    Card(onClick, align) {
        Row(modifier = Modifier.padding(8.dp), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Spacer(
                    Modifier.size(16.dp)
                        .border(2.dp, MaterialTheme.colorScheme.onSurface)
                        .background(item.colorFg.state)
                )
                Spacer(
                    Modifier.size(16.dp)
                        .border(2.dp, MaterialTheme.colorScheme.onSurface)
                        .background(item.colorBg.state)
                )
            }
            if (item.reward.state > 0) {
                LazyImage(
                    id = item.reward.state,
                    contentScale = ContentScale.FillBounds,
                    modifier = Modifier.height(36.dp)
                        .aspectRatio(
                            if (nnvl.orientation.state == App.Wheel.Theme.ORIENTATION_LAND) 16F / 9F
                            else 9F / 16F
                        )
                )
            } else {
                Icon(
                    imageVector = Svg.Image_plus,
                    contentDescription = null,
                    modifier = Modifier.size(36.dp),
                    tint = MaterialTheme.colorScheme.onSurface
                )
            }
        }
    }
}


@Composable
private fun AppEditorWheelScope.ItemSliders(
    item: AppEditorWheelScope.ItemsWrapper.Item,
    modifier: Modifier
) {
    Column(
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = modifier
    ) {
        FieldSlider(
            label = Res.string.layer_bottom_wheel_page_theme_custom_item_font_size,
            field = item.fontSize,
            modifier = Modifier.fillMaxWidth()
        )
        FieldSlider(
            label = Res.string.layer_bottom_wheel_page_theme_custom_item_name_y,
            field = item.nameY,
            modifier = Modifier.fillMaxWidth()
        )
        FieldSlider(
            label = Res.string.layer_bottom_wheel_page_theme_custom_item_icon_size,
            field = item.iconScale,
            modifier = Modifier.fillMaxWidth()
        )
        FieldSlider(
            label = Res.string.layer_bottom_wheel_page_theme_custom_item_icon_y,
            field = item.iconY,
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun AppEditorWheelScope.ItemColors(
    item: AppEditorWheelScope.ItemsWrapper.Item,
    horizontalOrder: Boolean = false,
    modifier: Modifier = Modifier.width(192.dp)
) {
    if (horizontalOrder) {

        Row(
            modifier = modifier,
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            ColorField(
                field = item.colorBg,
                label = Res.string.layer_bottom_wheel_page_theme_items_bg,
                modifier = Modifier.weight(1F)
            )
            ColorField(
                field = item.colorFg,
                label = Res.string.layer_bottom_wheel_page_theme_items_fg,
                modifier = Modifier.weight(1F)
            )
        }
    } else {
        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = modifier
        ) {
            ColorField(
                field = item.colorBg,
                label = Res.string.layer_bottom_wheel_page_theme_items_bg,
                modifier = Modifier.fillMaxWidth()
            )
            ColorField(
                field = item.colorFg,
                label = Res.string.layer_bottom_wheel_page_theme_items_fg,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun AppEditorWheelScope.ItemReward(
    nnvl: AppEditorWheelScope.Wrapper,
    item: AppEditorWheelScope.ItemsWrapper.Item,
    itemPickerKeyReward: String,
    boxModifier: Modifier
) {
    Card(shape = RoundedCornerShape(4.dp, 4.dp)) {
        Column {
            Text(
                text = Res.string.layer_bottom_wheel_page_theme_item_reward.rememberString(),
                style = MaterialTheme.typography.labelLarge,
                modifier = Modifier.padding(8.dp)
            )
            Box {
                AppWheelRendererReward(
                    nnvl = nnvl,
                    item = item,
                    boxModifier = boxModifier
                )
                if (item.reward.state != ResourceType.NO_IMAGE) {
                    SmallFloatingActionButton(
                        containerColor = MaterialTheme.colorScheme.errorContainer,
                        contentColor = MaterialTheme.colorScheme.onErrorContainer,
                        shape = CircleShape,
                        onClick = { item.reward.set(ResourceType.NO_IMAGE) },
                        modifier = Modifier.align(Alignment.TopEnd)
                            .padding(4.dp)
                    ) { Icon(Svg.Close, null) }
                }
                AddIconAction(
                    image = item.reward,
                    size = 192.dp,
                    orientation = nnvl.orientation.state,
                    itemPickerKeyReward = itemPickerKeyReward,
                    modifier = Modifier.align(Alignment.BottomEnd)
                )
            }
        }
    }
}

@Composable
private fun AppEditorWheelScope.PreviewItems(nnvl: AppEditorWheelScope.Wrapper, modifier: Modifier) {
    Box(
        modifier.fillMaxHeight()
            .background(
                color = MaterialTheme.colorScheme.surfaceContainer,
                shape = RoundedCornerShape(
                    topEnd = 8.dp,
                    bottomEnd = 8.dp
                )
            )
    ) {
        AppWheelRendererGame(nnvl, Modifier.align(Alignment.Center))
    }
}

@Composable
private fun AppEditorWheelScope.AddIconAction(
    image: EFieldRegularLong,
    size: Dp,
    orientation: Int,
    itemPickerKeyReward: String,
    modifier: Modifier
) {

    fun openResourcePicker() {
        GlobalObjectShare.launchImagePicker(
            key = itemPickerKeyReward,
            nav = nav,
            args = ResourceListingArgs(
                companyId = args.companyId,
                restrictionOrientation =
                    if (orientation == App.Wheel.Theme.ORIENTATION_LAND) {
                        ResourceOrientation.HORIZONTAL.mask
                    } else {
                        ResourceOrientation.VERTICAL.mask
                    },
                restrictionType = ResourceType.IMAGE.mask,
                restrictionCanIgnoreAspectRatio = true
            )
        ) {
            it.firstOrNull()?.apply {
                image.set(id)
            }
        }
    }

    if (size <= 192.dp) {
        SmallFloatingActionButton(
            onClick = { openResourcePicker() },
            modifier = modifier.padding(4.dp)
        ) {
            Icon(
                imageVector = Svg.Edit,
                contentDescription = null
            )
        }
    } else {
        FloatingActionButton(
            onClick = { openResourcePicker() },
            modifier = modifier.padding(4.dp)
        ) {
            Icon(
                imageVector = Svg.Edit,
                contentDescription = null
            )
        }
    }
}